import moment from "moment-timezone";

import CONSTANTS from "@/Constants";

interface DateLabelOptions {
  /**
   * Drop the year and lead with the day number, for the phone date row where
   * the label shares one line with four controls. The week boundary is
   * unchanged — compact is a formatting choice, not a different week.
   */
  compact?: boolean;
}

/**
 * Header label for the schedule board. Day view shows the single date;
 * week view shows the Monday-start (ISO) week range containing it.
 * Owned by SchedulePage's reactive title — nothing else should write
 * the .schedule-board-date element.
 */
export function scheduleBoardDateLabel(
  start: string | Date | undefined,
  isWeekView: boolean,
  { compact = false }: DateLabelOptions = {}
): string {
  const date = moment(start);
  if (!isWeekView) {
    return date.format(
      compact ? CONSTANTS.DATE_FORMAT_DAY_SHORT : "ddd MMM Do, YYYY"
    );
  }

  const weekStart = date.clone().startOf("isoWeek");
  const weekEnd = date.clone().endOf("isoWeek");
  const sameMonth = weekStart.month() === weekEnd.month();
  const sameYear = weekStart.year() === weekEnd.year();

  // Compact carries no year at all, including across a year seam. A phone row
  // 360px wide fits about 100px of label: "28 Dec 2026 – 3 Jan 2027" needs 176
  // and renders as "28 Dec 2026 – ...", which loses the end of the range
  // outright. The year is still on screen — the timeline header under this row
  // spells out the full date — so dropping it here costs nothing a reader
  // cannot see, and truncation costs a whole date.
  if (compact) {
    return `${weekStart.format(sameMonth ? "D" : "D MMM")} – ${weekEnd.format("D MMM")}`;
  }

  const startFormat = sameYear ? "MMM D" : "MMM D, YYYY";
  const endFormat = sameMonth && sameYear ? "D, YYYY" : "MMM D, YYYY";
  return `${weekStart.format(startFormat)} – ${weekEnd.format(endFormat)}`;
}
